import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isFirebaseConfigured } from '@/config/env';
import { routePaths } from '@/routes/paths';
import { useAuth } from '@/shared/hooks/useAuth';
import { isUserApproved } from '@/types/user';

type LocationState = {
  from?: string;
};

function mapAuthError(code: string | undefined): string {
  switch (code) {
    case 'auth/popup-closed-by-user':
      return 'Cerraste la ventana de Google. Intentá de nuevo.';
    case 'auth/popup-blocked':
      return 'El navegador bloqueó el popup. Permití ventanas emergentes para este sitio.';
    case 'auth/cancelled-popup-request':
      return 'Operación cancelada.';
    case 'auth/account-exists-with-different-credential':
      return 'Este email ya está registrado con otro método. Usá email y contraseña.';
    default:
      return 'No se pudo iniciar sesión con Google.';
  }
}

export function LoginPage() {
  const { signIn, signInWithGoogle, isAuthenticated, profile, loading } =
    useAuth();
  const location = useLocation();
  const from =
    (location.state as LocationState | null)?.from ?? routePaths.home;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  if (!isFirebaseConfigured()) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1>ERP ASAP</h1>
          <p className="auth-error">
            {import.meta.env.PROD
              ? 'Firebase no está en el build de producción. Ver docs/PAGES_DEPLOY.md y redeploy en Cloudflare Pages.'
              : 'Configurá Firebase en .env.local antes de iniciar sesión (ver .env.example).'}
          </p>
        </div>
      </div>
    );
  }

  if (!loading && isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  if (!loading && profile && !isUserApproved(profile)) {
    return <Navigate to={routePaths.accessPending} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch {
      setError('Email o contraseña incorrectos.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogleSignIn() {
    setError(null);
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code: string }).code)
          : undefined;
      setError(mapAuthError(code));
    } finally {
      setGoogleLoading(false);
    }
  }

  const busy = submitting || googleLoading;

  return (
    <div className="auth-screen">
      <div className="auth-card auth-card--wide">
        <h1>ERP ASAP</h1>
        <p className="auth-muted">Iniciá sesión para continuar</p>

        <button
          type="button"
          className="auth-button auth-button--google"
          disabled={busy}
          onClick={() => void handleGoogleSignIn()}
        >
          {googleLoading ? 'Conectando…' : 'Ingresar con Google'}
        </button>

        <div className="auth-divider">
          <span>o con email</span>
        </div>

        <form onSubmit={handleSubmit}>
          <label className="auth-label">
            Email
            <input
              type="email"
              autoComplete="email"
              required
              disabled={busy}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="auth-label">
            Contraseña
            <input
              type="password"
              autoComplete="current-password"
              required
              disabled={busy}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>

          {error ? <p className="auth-error">{error}</p> : null}

          <button type="submit" className="auth-button" disabled={busy}>
            {submitting ? 'Ingresando…' : 'Ingresar con email'}
          </button>
        </form>
      </div>
    </div>
  );
}
